/**
 * A `fetch` stand-in that reports upload progress.
 *
 * fetch cannot say how much of a request body has gone, so a 20 MB file on a
 * slow uplink sat behind one spinner for minutes with nothing to show it was
 * moving. XMLHttpRequest can. The answer comes back as a real `Response`, so
 * everything after the send reads it exactly as it reads fetch's, and it fails
 * the way fetch fails: a TypeError when no answer arrived, an AbortError when
 * the signal fired.
 *
 * Here rather than in one feature because more than one upload path uses it:
 * meeting shares and Presence chat.
 */

/** Bytes of the request body sent so far, out of its total. */
export type UploadProgress = (sent: number, total: number) => void

/** Statuses whose Response may not carry a body; the constructor throws on one that does. */
const NULL_BODY_STATUSES = new Set([204, 205, 304])

export function sendWithProgress(
  onProgress: UploadProgress,
): (url: string, init: RequestInit) => Promise<Response> {
  return (url, init) =>
    new Promise<Response>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open(init.method ?? 'GET', url)
      xhr.withCredentials = init.credentials === 'include'
      xhr.responseType = 'blob'
      new Headers(init.headers).forEach((value, name) => xhr.setRequestHeader(name, value))
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded, e.total)
      }
      xhr.onload = () => {
        // 0 is what a refused cross-origin answer looks like from here; fetch
        // rejects on it, and so does this.
        if (xhr.status < 200) {
          reject(new TypeError('Failed to fetch'))
          return
        }
        const type = xhr.getResponseHeader('Content-Type')
        resolve(
          new Response(NULL_BODY_STATUSES.has(xhr.status) ? null : (xhr.response as Blob), {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: type ? { 'Content-Type': type } : undefined,
          }),
        )
      }
      xhr.onerror = () => reject(new TypeError('Failed to fetch'))
      xhr.ontimeout = () => reject(new TypeError('Failed to fetch'))
      xhr.onabort = () => reject(new DOMException('The upload was cancelled.', 'AbortError'))
      if (init.signal?.aborted) {
        reject(new DOMException('The upload was cancelled.', 'AbortError'))
        return
      }
      init.signal?.addEventListener('abort', () => xhr.abort(), { once: true })
      xhr.send((init.body ?? null) as XMLHttpRequestBodyInit | null)
    })
}

/** True for the error a cancelled upload rejects with, from fetch or from the above. */
export function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError'
}
